import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import type { App } from '@hangar/shared';
import { useApps, useMe, useProjects } from '@/api/hooks';
import { useSigningCredentials, useSigningMutations } from '@/api/hooksSigning';
import type { SigningCredential } from '@/api/endpointsSigning';
import { ConfirmArchiveDialog } from '@/components/ConfirmArchiveDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SigningAppCard, type SlotActions } from '@/features/credentials/SigningAppCard';
import { SigningUploadDialog } from '@/features/credentials/SigningUploadDialog';
import { androidApps, iosApps, signingAccessOf, type SigningSlot } from '@/features/credentials/signingModel';
import type { SigningUploadTarget } from '@/features/credentials/useSigningUpload';
import { showMutationError } from '@/lib/mutationError';
import { useNow } from '@/lib/useNow';

/**
 * Credentials: the Apple signing material of the project's iOS apps (decision of 2026-10-22) and the upload keystore of its
 * Android apps (P13). Malible custodies them for the client: org admins and the project's maintainers see metadata, expiry
 * and health; only platform operators (Malible staff) upload, replace and revoke. The material itself never reaches the
 * browser after upload.
 */
export function CredentialsPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const projects = useProjects();
  const project = projects.data?.find((p) => p.slug === slug);
  const me = useMe();
  const access = signingAccessOf(me.data, project?.id);
  const apps = useApps(project?.id);
  const credentials = useSigningCredentials(project?.id, access.canRead);

  if ((projects.isLoading && !projects.data) || (apps.isLoading && !apps.data)) return <PageSkeleton />;
  if (projects.isError && !projects.data) return <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />;
  if (!project) return <EmptyState icon="account_tree" title="Project not found" description={`There is no project with slug “${slug}” in this organization.`} />;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader managed={!access.canManage} />
      {!access.canRead ? (
        <EmptyState icon="lock" title="Visible to admins and maintainers" description="Signing credentials of this project are shown to org admins and the project's maintainers." testId="signing-no-access" />
      ) : apps.isError ? (
        <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
      ) : (
        <SigningBody projectId={project.id} projectSlug={project.slug} ios={iosApps(apps.data ?? [])} android={androidApps(apps.data ?? [])} credentials={credentials} canManage={access.canManage} />
      )}
    </div>
  );
}

function PageHeader({ managed }: { managed: boolean }) {
  return (
    <header className="enter flex flex-col gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Credentials</h1>
        <p className="mt-1 text-sm text-muted-foreground">Signing material of the project's apps: the Apple distribution certificate, provisioning profile and App Store Connect key of each iOS app, and the upload keystore of each Android app. Hangar hands it to the build service at build time; nobody downloads it.</p>
      </div>
      {managed ? (
        <Alert data-testid="signing-managed-by-malible">
          <AlertTitle className="col-start-1 col-end-3 flex items-center gap-2">
            <Icon name="verified_user" size={18} />
            Managed by your Hangar administrators
          </AlertTitle>
          <AlertDescription className="col-start-1 col-end-3">Your Hangar administrators keep this material for your apps and replace it before it expires. To change it, contact them.</AlertDescription>
        </Alert>
      ) : null}
    </header>
  );
}

interface BodyProps {
  projectId: string;
  projectSlug: string;
  ios: App[];
  android: App[];
  credentials: ReturnType<typeof useSigningCredentials>;
  canManage: boolean;
}

function SigningBody({ projectId, projectSlug, ios, android, credentials, canManage }: BodyProps) {
  const now = useNow(60_000);
  const [upload, setUpload] = useState<SigningUploadTarget | null>(null);
  const [revoking, setRevoking] = useState<{ app: App; slot: SigningSlot; credential: SigningCredential } | null>(null);
  const { revoke } = useSigningMutations(projectId);
  if (ios.length === 0 && android.length === 0) {
    return (
      <EmptyState icon="phone_iphone" title="No mobile apps in this project" description="Signing material belongs to an iOS or Android app (native, Expo, React Native or Flutter)." testId="signing-no-apps"
        action={<Button asChild variant="outline"><Link to="/projects/$slug" params={{ slug: projectSlug }}>Open apps</Link></Button>} />
    );
  }
  if (credentials.isLoading && !credentials.data) return <Skeleton className="h-72 w-full rounded-lg" />;
  if (credentials.isError && !credentials.data) return <ErrorState error={credentials.error} onRetry={() => void credentials.refetch()} />;
  const actions: SlotActions | null = canManage ? { onUpload: (app, slot, current) => setUpload({ app, slot, current }), onRevoke: (app, slot, credential) => setRevoking({ app, slot, credential }) } : null;
  const confirmRevoke = async () => {
    if (!revoking) return;
    try {
      await revoke.mutateAsync(revoking.credential.id);
      setRevoking(null);
    } catch (e) {
      showMutationError(e, 'Could not revoke the credential');
    }
  };
  return (
    <>
      {ios.map((app) => (
        <SigningAppCard key={`${app.id}:ios`} app={app} platform="ios" credentials={credentials.data ?? []} now={now} actions={actions} />
      ))}
      {android.map((app) => (
        <SigningAppCard key={`${app.id}:android`} app={app} platform="android" credentials={credentials.data ?? []} now={now} actions={actions} />
      ))}
      <SigningUploadDialog projectId={projectId} target={upload} onClose={() => setUpload(null)} />
      <ConfirmArchiveDialog
        open={revoking !== null}
        onOpenChange={(open) => (open ? null : setRevoking(null))}
        title={`Revoke ${revoking?.slot.label.toLowerCase() ?? ''}?`}
        description={`Hangar deletes its encrypted copy at once.${revoking?.slot.kind === 'android_upload_keystore' ? ' Keep your own backup of the keystore: without it the app cannot be updated.' : ' Revoke it at Apple too if it was compromised.'}`}
        impact={[{ text: revoking?.slot.kind === 'android_upload_keystore' ? <>Hangar stops delivering the keystore for builds of <strong>{revoking.app.name}</strong> until a new one is uploaded</> : <>Signed builds of <strong>{revoking?.app.name}</strong> stop until new material is uploaded</> }, { text: <>Copies already with the build service stay there until replaced</> }]}
        confirmLabel="Revoke"
        busy={revoke.isPending}
        onConfirm={() => void confirmRevoke()}
        testId="signing-revoke-dialog"
      />
    </>
  );
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-72 w-full rounded-lg" />
    </div>
  );
}
